import { useState, type FormEvent } from 'react'
import { Crosshair, Loader2, MapPin } from 'lucide-react'
import { Field, Modal } from '@/shared/ui/Modal'
import { EmptyState, Note } from '@/shared/ui/states'
import { explain } from '@/shared/api/problem'
import { fmtNumber } from '@/shared/lib/format'
import { nearbyUnits } from './api'
import type { NearbyResponse } from '@/shared/api/types'

/**
 * "Which polling station am I at?" — the field query.
 *
 * Offers the browser's geolocation as well as manual entry, because the desk
 * case (paste coordinates from a report) and the field case (I am standing here)
 * are both real.
 */
export function NearbyDialog({
  open, onClose, onPick,
}: {
  open: boolean
  onClose: () => void
  onPick: (unitId: string) => void
}) {
  const [lat, setLat] = useState<number | ''>('')
  const [lon, setLon] = useState<number | ''>('')
  const [radius, setRadius] = useState(5000)
  const [results, setResults] = useState<NearbyResponse[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [locating, setLocating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function useMyLocation() {
    if (!('geolocation' in navigator)) {
      setError('This browser cannot report a location.')
      return
    }
    setLocating(true)
    setError(null)
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLat(Number(pos.coords.latitude.toFixed(6)))
        setLon(Number(pos.coords.longitude.toFixed(6)))
        setLocating(false)
      },
      (err) => {
        setError(err.code === err.PERMISSION_DENIED
          ? 'Location permission was denied. Enter coordinates manually.'
          : 'Could not get a location fix.')
        setLocating(false)
      },
      { enableHighAccuracy: true, timeout: 10_000 },
    )
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (lat === '' || lon === '') return
    setBusy(true)
    setError(null)
    try {
      setResults(await nearbyUnits(lat, lon, radius))
    } catch (err) {
      setError(explain(err))
      setResults(null)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Find nearby stations"
      description="Polling stations within a radius of a point, nearest first."
      footer={<button type="button" className="erp-btn" onClick={onClose}>Close</button>}
    >
      <form onSubmit={submit} className="space-y-4">
        {error && <Note tone="bad">{error}</Note>}

        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Latitude" required>
            <input
              type="number" step="0.000001" required
              value={lat}
              onChange={(e) => setLat(e.target.value === '' ? '' : Number(e.target.value))}
              className="erp-input w-full font-mono"
            />
          </Field>
          <Field label="Longitude" required>
            <input
              type="number" step="0.000001" required
              value={lon}
              onChange={(e) => setLon(e.target.value === '' ? '' : Number(e.target.value))}
              className="erp-input w-full font-mono"
            />
          </Field>
          <Field label="Radius (m)">
            <input
              type="number" min={100} max={100_000} step={100}
              value={radius}
              onChange={(e) => setRadius(Number(e.target.value))}
              className="erp-input w-full"
            />
          </Field>
        </div>

        <div className="flex items-center gap-2">
          <button type="button" className="erp-btn" onClick={useMyLocation} disabled={locating}>
            {locating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Crosshair className="h-3.5 w-3.5" />}
            Use my location
          </button>
          <button type="submit" className="erp-btn erp-btn-primary"
                  disabled={busy || lat === '' || lon === ''}>
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Search
          </button>
        </div>
      </form>

      {results && (
        <div className="mt-4">
          {results.length === 0 ? (
            <EmptyState
              icon={<MapPin className="h-4 w-4" />}
              title="Nothing within that radius"
              hint="Widen the radius, or check the coordinate order."
            />
          ) : (
            <ul className="border border-border rounded-md divide-y divide-border/60 max-h-64 overflow-y-auto">
              {results.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => { onPick(r.id); onClose() }}
                    className="w-full px-3 py-2 text-left hover:bg-muted/50 flex items-center gap-2"
                  >
                    <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <span className="min-w-0 flex-1">
                      <span className="text-erp-sm block truncate">{r.name}</span>
                      <span className="text-erp-xs font-mono text-muted-foreground block truncate">
                        {r.code} · {r.path}
                      </span>
                    </span>
                    <span className="text-erp-xs erp-metric text-muted-foreground shrink-0">
                      {r.distanceMetres < 1000
                        ? `${Math.round(r.distanceMetres)} m`
                        : `${fmtNumber(Number((r.distanceMetres / 1000).toFixed(1)))} km`}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Modal>
  )
}
